import React, { forwardRef } from 'react';
import { Box, Button, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Close, CloudUpload, ImageOutlined, SwapHoriz } from '@mui/icons-material';
import { BRAND_COLOR, FieldLabel } from './FormSection';

const CourseTitleImageRow = forwardRef(({
  titleValue,
  onTitleChange,
  image,
  imagePreview,
  onImageUpload,
  onRemoveImage,
  error
}, ref) => (
  <Stack ref={ref} direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mb: 3 }}>
    <Box sx={{ flex: 3, minWidth: 0 }}>
      <FieldLabel hint="Mínimo 1 carácter - Máximo 200" error={error}>
        Título del curso
      </FieldLabel>
      <TextField
        fullWidth
        value={titleValue || ''}
        onChange={(e) => onTitleChange(e.target.value)}
        placeholder="Escribe un título claro y atractivo..."
        error={Boolean(error)}
        inputProps={{ maxLength: 200 }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#fff',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: BRAND_COLOR,
              boxShadow: `0 0 0 3px ${BRAND_COLOR}26`,
            },
          },
        }}
      />
    </Box>

    <Box sx={{ flex: 2, minWidth: 0 }}>
      <FieldLabel hint="JPG o PNG, formato horizontal">Imagen de la tarjeta</FieldLabel>
      {image && imagePreview ? (
        <Box
          sx={{
            position: 'relative',
            height: { xs: 160, md: 160 },
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#f4f6f6',
            '&:hover .image-actions': { opacity: 1 },
          }}
        >
          <img
            src={imagePreview}
            alt="Vista previa"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Stack
            className="image-actions"
            direction="row"
            spacing={1}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              opacity: { xs: 1, md: 0 },
              transition: 'opacity .15s',
            }}
          >
            <Tooltip title="Cambiar imagen" arrow>
              <IconButton
                component="label"
                size="small"
                sx={{ bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
              >
                <SwapHoriz fontSize="small" />
                <input type="file" hidden onChange={onImageUpload} accept="image/*" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Quitar imagen" arrow>
              <IconButton
                size="small"
                onClick={onRemoveImage}
                sx={{ bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {image instanceof File && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                color: '#fff',
                bgcolor: 'rgba(0,0,0,0.6)',
                maxWidth: 'calc(100% - 16px)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {image.name}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          component="label"
          sx={{
            height: { xs: 160, md: 160 },
            borderRadius: 2,
            border: '2px dashed',
            borderColor: `${BRAND_COLOR}80`,
            bgcolor: `${BRAND_COLOR}0A`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            cursor: 'pointer',
            transition: 'all .15s',
            '&:hover': { borderColor: BRAND_COLOR, bgcolor: `${BRAND_COLOR}14` },
          }}
        >
          <ImageOutlined sx={{ fontSize: 40, color: BRAND_COLOR }} />
          <Typography variant="body2" color="text.secondary">
            Haz clic para seleccionar una imagen
          </Typography>
          <Button
            component="span"
            size="small"
            variant="contained"
            startIcon={<CloudUpload />}
            sx={{ bgcolor: BRAND_COLOR, '&:hover': { bgcolor: '#3d9993' }, fontWeight: 600 }}
          >
            Subir archivo
          </Button>
          <input type="file" hidden onChange={onImageUpload} accept="image/*" />
        </Box>
      )}
    </Box>
  </Stack>
));

export default CourseTitleImageRow;
